import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { StepperModule } from 'primeng/stepper';
import { ToastModule } from 'primeng/toast';
import { FileUpload } from 'primeng/fileupload';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { FormFooter } from "src/app/shared/components/form-footer/form-footer";
import { ActivatedRoute, Router } from '@angular/router';
import { Routes } from 'src/app/core/routes/routes';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { BusinessApi } from '@features/business/services/business-api';
import { DataEndpoint } from '@core/models/general.model';
import { BusinessCreateRequestModel, BusinessDetailModel, BusinessGroupedModel } from '@features/business/models/business.model';
import { MessageService } from 'primeng/api';
import { FormUtils } from '@shared/utils/form.utils';
import { DatePicker } from 'primeng/datepicker';
import moment from 'moment';
import { SectorService, SectorOption } from '@shared/services/sector-service/sector-service';
import { BusinessLogo } from "@shared/components/business-logo/business-logo";
import { AppDialog } from "@shared/components/dialog";
import { BlockUI } from "primeng/blockui";
import { ProgressSpinner } from "primeng/progressspinner";
import { BusinessStateService } from '@core/services/business-state.service';

@Component({
  selector: 'app-business-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    SelectModule,
    ButtonModule,
    MessageModule,
    StepperModule,
    FileUpload,
    ToastModule,
    MultiSelectModule,
    FormFooter,
    DatePicker,
    BusinessLogo,
    AppDialog,
    BlockUI,
    ProgressSpinner
],
  templateUrl: './business-form.html',
  styleUrl: './business-form.scss',
  providers: [MessageService]
})
export class BusinessForm {
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly messageService = inject(MessageService);
  readonly sectorService = inject(SectorService);
  readonly businessApi = inject(BusinessApi);
  private readonly businessStateService = inject(BusinessStateService);

  isEditMode = signal(false);

  businessGroupOptions: BusinessGroupedModel[] = []

  businessSectorOptions: SectorOption[] = []

  form: FormGroup = new FormGroup({
    parentId: new FormControl(null, [Validators.required]),
    logo: new FormControl(null, [Validators.required]),
    name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    description: new FormControl('', [Validators.required, Validators.maxLength(1000)]),
    sector: new FormControl<string[]>([], [Validators.required]),
    identification: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    contactName: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    contactPhone: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    contactEmail: new FormControl('', [Validators.required, Validators.email]),
    expirationDate: new FormControl('', [Validators.required]),
    maxUsers: new FormControl(1, [Validators.required, Validators.pattern('^[0-9]*$')])
  });

  businessId: string | null = null;
  isFormSubmitted = false;
  isLoading = false;
  activeStep: number = 1;
  uploadedFiles: any[] = [];
  fileUrl: string = '';
  fileName: string = '';

  visibleSuccessDialog: boolean = false;
  dialogTitle: string = '';
  dialogMessage: string = '';

  ngOnInit() {
    this.getBusinessGroupList();
    this.loadSectors();
    this.checkMode();
  }

  loadSectors() {
    this.sectorService.getSectors().subscribe({
      next: (sectors) => {
        this.businessSectorOptions = sectors;
      },
      error: (error: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.Message
        });
      }
    });
  }

  getBusinessGroupList() {
    this.isLoading = true;

    this.businessApi.getBusinessGrouped().subscribe({
      next: (response: DataEndpoint<BusinessGroupedModel[]>) => {
        if (response.Status === 200) {
          this.businessGroupOptions = response.Data ?? [];
          if (!this.isEditMode()) {
            this.form.patchValue({parentId: this.businessGroupOptions[0].Id});
          }
          this.isLoading = false;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    })
  }

  private checkMode(): void {
    this.businessId = this.route.snapshot.paramMap.get('id');

    if (this.businessId) {
      this.isEditMode.set(true);
      this.loadBusinessData(this.businessId);
    } else {
      this.isEditMode.set(false);
    }
  }

  private loadBusinessData(id: string): void {
    this.isLoading = true;

    this.businessApi.getBusinessDetail(id).subscribe({
      next: (response: DataEndpoint<BusinessDetailModel>) => {
        if (response.Status === 200) {
          this.setEditForm(response.Data!);
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message,
          life: 3000
        });
        this.isLoading = false;
      }
    });
  }

  private setEditForm(data: BusinessDetailModel) {
    const selectedSectors = data.Sector || [];

    this.form.patchValue({
      parentId: data.ParentId,
      logo: data.LogoUrl,
      name: data.Name,
      description: data.Description,
      sector: selectedSectors,
      identification: data.Identification,
      contactName: data.ContactName,
      contactPhone: data.ContactPhone,
      contactEmail: data.ContactEmail,
      expirationDate: new Date(data.ExpirationDate),
      maxUsers: data.MaxUsers
    })
    this.fileUrl = data.LogoUrl;
    this.fileName = data.Logo;
  }

   // Validación del formulario antes de avanzar
   canGoNext(): boolean {
    return this.form.valid;
  }

  // Avanzar al siguiente paso
  onClickNextStep(): void {
    if (!this.canGoNext()) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.activeStep < 2) {
      this.onSaveForm();
    }
  }

  // Retroceder al paso anterior
  onClickPreviousStep(): void {
    if (this.activeStep > 1) {
      this.activeStep--;
    }
  }

  onClickCancel(): void {
    this.router.navigate([Routes.businessesPage]);
  }

  // Helpers para deshabilitar botones
  isFirstStep(): boolean {
    return this.activeStep === 1;
  }

  isLastStep(): boolean {
    return this.activeStep === 2;
  }

  onSelectFile(event: any) {
    for (let file of event.files) {
      this.uploadedFiles.push(file);
      this.form.patchValue({logo: file});
      this.fileUrl = file.objectURL;
      this.fileName = file.name;
    }
  }

  removeLogo() {
    this.form.get('logo')?.setValue('')
    this.fileUrl = '';
    this.fileName = '';
  }

  showCreateSuccessDialog() {
    this.dialogTitle = this.isEditMode()
      ? '¡Empresa actualizada!'
      : '¡Empresa creada!';
    this.dialogMessage = this.isEditMode()
      ? 'La empresa ha sido actualizada exitosamente.'
      : 'La empresa ha sido creada exitosamente.';
    this.visibleSuccessDialog = true;
  }

  onSaveForm() {
    if (this.form.valid) {
      const request: BusinessCreateRequestModel = {
        IsGroup: false,
        ParentId: this.form.get('parentId')?.value,
        Name: this.form.get('name')?.value,
        ContactName: this.form.get('contactName')?.value,
        ContactPhone: this.form.get('contactPhone')?.value,
        ContactEmail: this.form.get('contactEmail')?.value,
        Description: this.form.get('description')?.value,
        Sector: this.form.get('sector')?.value,
        Identification: this.form.get('identification')?.value,
        ExpirationDate: moment(this.form.get('expirationDate')?.value).format(),
        MaxUsers: this.form.get('maxUsers')?.value,
        Logo: this.form.get('logo')?.value
      }

      const formData = FormUtils.toFormData(request);

      this.isLoading = true;

      let requestUrl = this.businessApi.postBusiness(formData);
      if (this.isEditMode() && this.businessId) {
        requestUrl = this.businessApi.updateBusiness(this.businessId, formData);
      }

      requestUrl.subscribe({
        next: (response: DataEndpoint<any>) => {
          if (response.Success) {
            this.businessStateService.refreshBusinessList();
            this.showCreateSuccessDialog();
            this.activeStep++;
          }
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.error.Message });
        }
      })
    }
    else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'El formulario es inválido. Por favor, revisar.' });
    }
  }

  onCloseDialog() {
    this.visibleSuccessDialog = false;
    this.router.navigate([Routes.businessesPage]);
  }

  isInvalid(controlName: string) {
    const control = this.form.get(controlName);
    return control?.invalid && (control.touched || this.isFormSubmitted);
  }

  getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    return FormUtils.getErrorMessage(control);
  }
}
